/* Display estimates only. Shopify remains the USD price/cart/checkout owner. */
(() => {
  'use strict';
  const country = document.querySelector('meta[name="renowa-country"]')?.content;
  const baseCurrency = document.querySelector('meta[name="renowa-charge-currency"]')?.content;
  const currencyCountries = {
    EUR: 'AX AD AT BE BG HR CY EE FI FR GF DE GR GP IE IT XK LV LT LU MT MQ MC ME NL PT SM SK SI ES BL MF PM VA',
    USD: 'BQ EC SV PA TC US VG',
    GBP: 'GB GG IM JE',
    XCD: 'AI AG DM GD MS KN LC VC',
    DKK: 'DK FO GL', NOK: 'NO SJ', CHF: 'CH LI',
    ALL: 'AL', ARS: 'AR', AWG: 'AW', BSD: 'BS', BBD: 'BB', BYN: 'BY', BZD: 'BZ', BMD: 'BM',
    BOB: 'BO', BAM: 'BA', BRL: 'BR', CAD: 'CA', KYD: 'KY', CLP: 'CL', COP: 'CO', CRC: 'CR',
    XCG: 'CW SX', CZK: 'CZ', DOP: 'DO', FKP: 'FK', GIP: 'GI', GTQ: 'GT', GYD: 'GY',
    HTG: 'HT', HNL: 'HN', HUF: 'HU', ISK: 'IS', JMD: 'JM', MXN: 'MX', MDL: 'MD',
    NIO: 'NI', MKD: 'MK', PYG: 'PY', PEN: 'PE', PLN: 'PL', RON: 'RO', RUB: 'RU', RSD: 'RS',
    SRD: 'SR', SEK: 'SE', TTD: 'TT', TRY: 'TR', UYU: 'UY', VES: 'VE'
  };
  const currencies = Object.fromEntries(Object.entries(currencyCountries).flatMap(([currency, countries]) => countries.split(' ').map(code => [code, currency])));
  const target = currencies[country] || 'USD';
  const locale = document.documentElement.lang || 'en';
  const usd = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' });
  const local = new Intl.NumberFormat(locale, { style: 'currency', currency: target });
  const digits = local.resolvedOptions().maximumFractionDigits;
  let rates;
  const active = () => baseCurrency === 'USD' && target !== 'USD' && Number.isFinite(rates?.[target.toLowerCase()]) && rates[target.toLowerCase()] > 0;
  const wholeUsd = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const usdText = (cents, whole = false) => `${(whole && cents % 100 === 0 ? wholeUsd : usd).format(cents / 100)} USD`;
  function rounded(value, exact) {
    const scale = 10 ** digits;
    if (exact) return Math.round((value + Number.EPSILON) * scale) / scale;
    if (!value) return 0;
    if (!digits) return Math.ceil(value - 1e-9);
    // The smallest price ending in .99 that is no lower than the unrounded estimate.
    return Math.ceil(value - .99 - 1e-9) + .99;
  }
  function paint(node) {
    const raw = node.dataset.rnMoney;
    if (raw === '' || raw == null) return;
    const cents = Number(raw);
    if (!Number.isSafeInteger(cents) || cents < 0) return;
    if (!node.dataset.rnUsdText) node.dataset.rnUsdText = node.textContent;
    if (active()) {
      const amount = rounded(cents / 100 * rates[target.toLowerCase()], node.hasAttribute('data-rn-exact'));
      node.textContent = `≈ ${local.format(amount)} ${target}`;
      node.title = usdText(cents);
    } else {
      node.textContent = node.closest('[data-rn-whole-money]') ? usdText(cents, true) : node.dataset.rnUsdText;
      node.removeAttribute('title');
    }
  }
  function refresh() {
    document.querySelectorAll('[data-rn-money]').forEach(paint);
    document.querySelectorAll('[data-rn-fx-hint]').forEach(node => { node.hidden = !active(); });
    document.querySelectorAll('select[name="country_code"] option').forEach(option => {
      const currency = currencies[option.value] || 'USD';
      // Never label a USD fallback as a local-currency price.
      const shown = option.value === country ? (active() ? target : 'USD') : currency;
      option.textContent = `${option.value} / ${shown}${shown === 'USD' ? '' : ' ≈'}`;
    });
    const legacyLabel = document.getElementById('currency-label');
    if (legacyLabel) legacyLabel.textContent = active() ? `${target} ≈` : baseCurrency;
  }
  window.RenowaMoney = Object.freeze({
    set(parent, value, fallback) {
      if (!parent) return;
      const node = parent.matches('[data-rn-money]') ? parent : parent.querySelector('[data-rn-money]') || parent;
      if (value == null || value === '') {
        node.removeAttribute('data-rn-money');
        delete node.dataset.rnUsdText;
        node.removeAttribute('title');
        node.textContent = fallback;
        return;
      }
      node.dataset.rnMoney = value;
      node.dataset.rnUsdText = fallback;
      paint(node);
    },
    charge(root, value) {
      if (value == null || value === '' || !Number.isSafeInteger(Number(value))) return;
      root.querySelectorAll('[data-rn-charge]').forEach(node => { node.textContent = usdText(Number(value), !!node.closest('[data-rn-whole-money]')); });
    }
  });
  document.addEventListener('shopify:section:load', refresh);
  refresh();
  if (baseCurrency !== 'USD' || target === 'USD') return;
  const cacheKey = 'renowa:usd-estimate-rates:v1';
  function valid(data) {
    const timestamp = Date.parse(`${data?.date}T00:00:00Z`);
    const age = Date.now() - timestamp;
    return Number.isFinite(timestamp) && age >= -86400000 && age < 259200000 && data?.usd?.usd === 1 && Number.isFinite(data.usd[target.toLowerCase()]) && data.usd[target.toLowerCase()] > 0;
  }
  async function load() {
    try {
      const saved = JSON.parse(localStorage.getItem(cacheKey));
      if (saved && Date.now() >= saved.savedAt && Date.now() - saved.savedAt < 86400000 && valid(saved.data)) {
        rates = saved.data.usd;
        refresh();
        return;
      }
    } catch { /* Storage can be blocked; the converter remains optional. */ }
    for (const endpoint of [
      'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json',
      'https://latest.currency-api.pages.dev/v1/currencies/usd.min.json'
    ]) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(endpoint, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal });
        if (!response.ok) throw new Error('Exchange rates unavailable');
        const data = await response.json();
        if (!valid(data)) throw new Error('Exchange rates invalid or expired');
        rates = data.usd;
        try { localStorage.setItem(cacheKey, JSON.stringify({ savedAt: Date.now(), data })); } catch { /* No storage is required for checkout. */ }
        refresh();
        return;
      } catch { /* Try the independent mirror, then leave native USD untouched. */ }
      finally { clearTimeout(timer); }
    }
  }
  void load();
})();

/* Enlarged certificate viewer for product compliance documents. */
(() => {
  'use strict';
  function initCertificates(scope = document) {
    scope.querySelectorAll('[data-rn-certificates]').forEach(root => {
      if (root.dataset.rnCertificatesReady === 'true') return;
      root.dataset.rnCertificatesReady = 'true';
      const dialog = root.querySelector('[data-rn-certificate-dialog]');
      const image = root.querySelector('[data-rn-certificate-image]');
      const title = root.querySelector('[data-rn-certificate-title]');
      const full = root.querySelector('[data-rn-certificate-full]');
      if (!dialog || !image || !title || !full) return;
      root.addEventListener('click', event => {
        const button = event.target.closest('[data-rn-certificate-open]');
        if (!button || !root.contains(button)) return;
        image.src = button.dataset.src;
        image.alt = button.dataset.title;
        title.textContent = button.dataset.title;
        full.href = button.dataset.src;
        dialog.showModal();
      });
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      });
    });
  }
  initCertificates();
  document.addEventListener('shopify:section:load', event => initCertificates(event.target));
})();

/* Progressive enhancement: Shopify owns product IDs, cart, localization and checkout. */
(() => {
  let controller;
  let observers = [];
  function init() {
    controller?.abort();
    observers.forEach(observer => observer.disconnect());
    observers = [];
    controller = new AbortController();
    const on = (el, event, handler) => el?.addEventListener(event, handler, { signal: controller.signal });
    document.querySelectorAll('[data-rn-localization]').forEach(select => on(select, 'change', () => select.form.requestSubmit()));
    document.querySelectorAll('.rn-mobile-nav').forEach(menu => {
      on(menu, 'keydown', e => {
        if (e.key === 'Escape') { menu.open = false; menu.querySelector('summary').focus(); }
      });
      on(document, 'click', e => { if (!menu.contains(e.target)) menu.open = false; });
    });
    document.querySelectorAll('[data-rn-discovery]').forEach(root => {
      const filters = root.querySelector('[data-rn-area-filters]');
      if (!filters) return;
      const buttons = [...filters.querySelectorAll('[data-rn-area]')];
      const panels = [...root.querySelectorAll('[data-rn-area-panel]')];
      const choose = button => {
        buttons.forEach(other => other.setAttribute('aria-pressed', String(other === button)));
        panels.forEach(panel => { panel.hidden = panel.id !== button.dataset.rnArea; });
      };
      buttons.forEach(button => on(button, 'click', () => {
        choose(button);
        root.querySelector('[data-rn-area-status]').textContent = button.textContent;
      }));
      if (buttons[0]) choose(buttons[0]);
      filters.hidden = false;
      root.classList.add('rn-discovery--enhanced');
    });
    document.querySelectorAll('[data-rn-product]').forEach(root => {
      const select = root.querySelector('[data-rn-variant]');
      const mediaAliases = new Map();
      const supplierImages = new Map();
      const slides = [...root.querySelectorAll('[data-media-id]')].filter(slide => {
        const image = slide.querySelector('img');
        // DSers can import the same AliExpress asset again with a Shopify UUID.
        // Match only that supplier hash format; leave other filenames and video intact.
        const filename = image?.getAttribute('src')?.split('/').pop()?.split('?')[0];
        const key = filename?.split('_')[0]?.split('.')[0];
        if (!root.classList.contains('rn-product') || !/^S[a-f0-9]{32}[a-z0-9]$/i.test(key || '')) return true;
        const original = supplierImages.get(key);
        slide.hidden = Boolean(original);
        if (original) { mediaAliases.set(slide.dataset.mediaId, original); return false; }
        supplierImages.set(key, slide.dataset.mediaId);
        return true;
      });
      const gallery = slides[0]?.parentElement;
      const links = [...root.querySelectorAll('[data-rn-media-link]')];
      links.forEach(link => { link.hidden = mediaAliases.has(link.dataset.rnMediaLink); });
      const counter = root.querySelector('[data-rn-gallery-counter]');
      let activeIndex = 0;
      function markMedia(index) {
        activeIndex = Math.max(0, Math.min(index, slides.length - 1));
        links.forEach(link => {
          if (link.dataset.rnMediaLink === slides[activeIndex]?.dataset.mediaId) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
        if (counter) counter.textContent = `${activeIndex + 1} / ${slides.length}`;
      }
      function showMedia(id) {
        id = mediaAliases.get(id) || id;
        const index = slides.findIndex(media => media.dataset.mediaId === id);
        if (index < 0 || !gallery) return;
        gallery.scrollTo({ left: slides[index].offsetLeft - gallery.offsetLeft, behavior: 'instant' });
        markMedia(index);
      }
      const packages = root.querySelector('[data-rn-packages]');
      if (packages) {
        const updatePackage = () => {
          const choice = packages.querySelector('[data-rn-package]:checked');
          if (!choice) return;
          window.RenowaMoney.charge(root, choice.dataset.priceCents);
          ['[data-rn-price]', '[data-rn-mobile-price]', '[data-rn-dock-price]'].forEach(selector => {
            const price = root.querySelector(selector);
            if (price) window.RenowaMoney.set(price, choice.dataset.priceCents, choice.dataset.price);
          });
          const button = packages.form?.querySelector('[data-rn-add]');
          if (button) {
            button.disabled = choice.disabled;
            const label = button.querySelector('[data-rn-package-label]');
            if (label) label.textContent = choice.disabled ? button.dataset.unavailableLabel : button.dataset.availableLabel;
          }
          const offer = root.querySelector('[data-rn-package-offer]');
          const compare = root.querySelector('[data-rn-package-compare]');
          const priceCents = Number(choice.dataset.priceCents);
          const compareCents = Number(choice.dataset.compareCents);
          const hasOffer = Number.isSafeInteger(compareCents) && compareCents > priceCents;
          if (offer && compare) {
            offer.hidden = !hasOffer;
            window.RenowaMoney.set(compare, hasOffer ? compareCents : null, hasOffer ? choice.dataset.compare : '');
            const sale = offer.querySelector('[data-rn-package-sale]');
            if (sale) sale.textContent = choice.dataset.salePercent || '';
          }
          showMedia(choice.dataset.media);
        };
        on(packages, 'change', updatePackage);
        updatePackage();
      }
      on(select, 'change', () => {
        const variant = select.selectedOptions[0];
        if (!variant) return;
        window.RenowaMoney.charge(root, variant.dataset.priceCents);
        const button = root.querySelector('[data-rn-add]');
        const quantity = root.querySelector('[data-rn-quantity]');
        const price = root.querySelector('[data-rn-price]');
        const dockPrice = root.querySelector('[data-rn-dock-price]');
        if (price) window.RenowaMoney.set(price, variant.dataset.priceCents, variant.dataset.price);
        const offer = root.querySelector('[data-rn-offer]');
        const compare = root.querySelector('[data-rn-compare]');
        if (offer && compare) {
          window.RenowaMoney.set(compare, variant.dataset.compareCents, variant.dataset.compare || '');
          offer.hidden = !variant.dataset.compare;
        }
        if (dockPrice) window.RenowaMoney.set(dockPrice, variant.dataset.priceCents, variant.dataset.price);
        const mobilePrice = root.querySelector('[data-rn-mobile-price]');
        if (mobilePrice) window.RenowaMoney.set(mobilePrice, variant.dataset.priceCents, variant.dataset.price);
        if (button) {
          button.disabled = variant.dataset.available !== 'true';
          button.textContent = button.disabled ? button.dataset.unavailableLabel : button.dataset.availableLabel;
        }
        const accelerated = root.querySelector('.rn-accelerated');
        if (accelerated) accelerated.hidden = variant.dataset.available !== 'true';
        if (quantity) {
          quantity.min = variant.dataset.min || '1';
          quantity.step = variant.dataset.step || '1';
          if (variant.dataset.max) quantity.max = variant.dataset.max; else quantity.removeAttribute('max');
          quantity.value = quantity.min;
        }
        root.querySelectorAll('[data-rn-variant-choice]').forEach(choice => choice.setAttribute('aria-pressed', String(choice.dataset.rnVariantChoice === variant.value)));
        showMedia(variant.dataset.media);
        if (root.dataset.rnUpdateUrl !== 'false') {
          const url = new URL(location.href);
          url.searchParams.set('variant', variant.value);
          history.replaceState({}, '', url);
        }
      });
      const choices = root.querySelector('[data-rn-variant-choices]');
      if (select && choices) {
        const field = select.closest('.rn-variant-field');
        if (field) {
          field.before(choices);
          field.hidden = true;
          choices.hidden = false;
          choices.querySelectorAll('[data-rn-variant-choice]').forEach(choice => on(choice, 'click', () => {
            select.value = choice.dataset.rnVariantChoice;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }));
        }
      }
      links.forEach(link => on(link, 'click', e => { e.preventDefault(); showMedia(link.dataset.rnMediaLink); }));
      root.querySelectorAll('[data-rn-gallery-step]').forEach(button => on(button, 'click', () => {
        const next = (activeIndex + Number(button.dataset.rnGalleryStep) + slides.length) % slides.length;
        showMedia(slides[next]?.dataset.mediaId);
      }));
      if (gallery && slides.length) {
        markMedia(0);
        on(gallery, 'scroll', () => markMedia(Math.round(gallery.scrollLeft / gallery.clientWidth)));
        const controls = root.querySelector('[data-rn-gallery-controls]');
        if (controls) controls.hidden = false;
      }
      // First load shows the product photograph, not the supplier's variant packaging.
      if (select && new URL(location.href).searchParams.has('variant')) showMedia(select.selectedOptions[0]?.dataset.media);
      const dock = root.querySelector('[data-rn-product-dock]');
      const panel = root.querySelector('.rn-pdp-purchase');
      if (dock && panel && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          const entry = entries[0];
          dock.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
        });
        observer.observe(panel);
        observers.push(observer);
      }
    });
  }
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
})();
